import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { api } from '@/api';

interface User {
  id: string;
  email: string;
  displayName: string | null;
  role: string;
}

interface Mailbox {
  id: string;
  address: string;
  displayName: string | null;
  isPrimary: boolean;
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null);
  const mailboxes = ref<Mailbox[]>([]);
  const initialized = ref(false);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Getters
  const isAuthenticated = computed(() => user.value !== null);
  const isAdmin = computed(() => user.value?.role === 'admin');
  const primaryMailbox = computed(() => mailboxes.value.find(m => m.isPrimary) || mailboxes.value[0] || null);

  // Actions
  async function checkAuth() {
    if (initialized.value) return;

    loading.value = true;
    error.value = null;

    try {
      const response = await api.get('/api/v1/auth/me');
      user.value = response.user;
      mailboxes.value = response.mailboxes || [];
    } catch (e) {
      // Not authenticated, that's fine
      user.value = null;
    } finally {
      loading.value = false;
      initialized.value = true;
    }
  }

  async function login(email: string, password: string) {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.post('/api/v1/auth/login', {
        email,
        password,
      });

      user.value = response.user;

      // Store CSRF token if provided
      const csrfToken = response.csrfToken;
      if (csrfToken) {
        localStorage.setItem('csrfToken', csrfToken);
      }

      // Fetch full user data including mailboxes
      const meResponse = await api.get('/api/v1/auth/me');
      user.value = meResponse.user;
      mailboxes.value = meResponse.mailboxes || [];
      initialized.value = true;

      return true;
    } catch (e) {
      error.value = (e as Error).message || 'Login failed';
      return false;
    } finally {
      loading.value = false;
    }
  }

  async function logout() {
    loading.value = true;

    try {
      await api.post('/api/v1/auth/logout');
    } catch (e) {
      // Ignore errors during logout
    } finally {
      user.value = null;
      mailboxes.value = [];
      initialized.value = false;
      localStorage.removeItem('csrfToken');
      loading.value = false;
    }
  }

  function clearError() {
    error.value = null;
  }

  return {
    // State
    user,
    mailboxes,
    initialized,
    loading,
    error,
    // Getters
    isAuthenticated,
    isAdmin,
    primaryMailbox,
    // Actions
    checkAuth,
    login,
    logout,
    clearError,
  };
});
